import { WEB_BUILD_ID_DEV } from '@/lib/config/web-build'

/** Last reload trigger (`mount` | `resume`) — diagnostic only. */
export const WEB_DEPLOY_RELOAD_KEY = 'alumnia:web-deploy-reload'

/** Reload attempts this WebView session — caps loops if cache bust fails. */
export const WEB_DEPLOY_RELOAD_ATTEMPTS_KEY = 'alumnia:web-deploy-reload-attempts'

/** Max deploy-driven reloads per session (WKWebView cache can need >1 try). */
export const MAX_WEB_DEPLOY_RELOAD_ATTEMPTS = 3

/**
 * Build id baked into the currently running client bundle (`next.config` env).
 * Null when unset (e.g. some test environments).
 */
export function readClientWebBuildId(): string | null {
  const id = process.env.NEXT_PUBLIC_WEB_BUILD_ID?.trim()
  return id || null
}

/**
 * Returns true when the server has shipped a newer web deploy than this WebView
 * session booted with. Skips reload when either side is the dev sentinel or ids
 * are missing (fail-open).
 */
export function shouldReloadForWebBuild(
  clientBuildId: string | null | undefined,
  serverBuildId: string | null | undefined
): boolean {
  if (!clientBuildId || !serverBuildId) return false
  if (clientBuildId === WEB_BUILD_ID_DEV && serverBuildId === WEB_BUILD_ID_DEV) return false
  return clientBuildId !== serverBuildId
}

export type AppVersionPayload = {
  webBuildId?: string
  minimumVersion?: string
  currentVersion?: string
}

export function parseWebBuildIdFromAppVersionPayload(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null
  const id = (data as AppVersionPayload).webBuildId
  return typeof id === 'string' && id.length > 0 ? id : null
}

export function parseReloadAttemptCount(raw: string | null | undefined): number {
  if (raw == null || raw === '') return 0
  const n = Number.parseInt(raw, 10)
  if (!Number.isFinite(n) || n < 0) return 0
  return n
}

export function canAttemptWebDeployReload(attemptCount: number): boolean {
  return attemptCount < MAX_WEB_DEPLOY_RELOAD_ATTEMPTS
}

/**
 * Cache-bust query param for WKWebView — plain `reload()` can re-serve disk cache.
 */
export function buildCacheBustedReloadUrl(href: string, nonce: string): string {
  const url = new URL(href)
  url.searchParams.set('_wd', nonce)
  return url.toString()
}

/**
 * Freshness-probe URL for `/api/app-version`.
 *
 * WKWebView's on-disk `NSURLCache` routinely ignores the Fetch `cache: 'no-store'`
 * mode AND the response's `no-store` header, re-serving a stale `app-version`
 * payload (with the OLD `webBuildId`) for the life of the disk cache — which
 * survives app restarts. A stale probe makes `shouldReloadForWebBuild` read an
 * old build id that matches the old client, so the deploy-driven reload never
 * fires and the user is stranded on the old bundle no matter how many times they
 * relaunch. A unique query param per poll defeats the URL-keyed cache: WKWebView
 * has no cached entry for `?_t=<nonce>`, so it must hit the network and return the
 * true latest build id. This is the reliable workaround when cache directives are
 * ignored.
 */
export function buildAppVersionProbeUrl(nonce: string): string {
  return `/api/app-version?_t=${encodeURIComponent(nonce)}`
}
